import {
  useClick,
  useDismiss,
  useInteractions,
  autoUpdate,
  flip,
  offset,
  Placement,
  useFloating,
  useHover,
  safePolygon,
  shift,
  size
} from '@floating-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import React, { HTMLAttributes, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

/** Prompt backdrop uses z-index 9_999_999 in `Prompt.styled.tsx`; tooltips portal above it. */
const DEFAULT_TOOLTIP_Z_INDEX = 10_000_000

interface Props extends HTMLAttributes<HTMLSpanElement> {
  tooltip?: React.ReactNode
  renderTooltip?: (isOpen: boolean) => JSX.Element
  placement?: Placement
  openDelay?: number
  closeDelay?: number
  maxWidth?: number
  zIndex?: number
  openOnClick?: boolean
}

export const TextTooltip: React.FC<Props> = ({
  tooltip,
  renderTooltip,
  placement = 'bottom-end',
  children,
  openDelay = 200,
  closeDelay = 600,
  maxWidth = 300,
  zIndex = DEFAULT_TOOLTIP_Z_INDEX,
  openOnClick = false,
  ...rest
}) => {
  if (!tooltip && !renderTooltip) {
    throw new Error('Either tooltip or renderTooltip prop must be provided')
  }

  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))
  const [infoOpen, setInfoOpen] = useState(false)

  const { context, x, y, refs, strategy } = useFloating({
    open: infoOpen,
    onOpenChange: setInfoOpen,
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          const maxHeight = Math.min(availableHeight, 400)
          elements.floating.style.setProperty('--tooltip-max-height', `${maxHeight}px`)
        }
      })
    ]
  })

  const click = useClick(context, { enabled: openOnClick, ignoreMouse: true })
  const { getReferenceProps } = useInteractions([click])

  useHover(context, {
    mouseOnly: true,
    delay: {
      open: openDelay,
      close: closeDelay
    },
    handleClose: safePolygon()
  })

  useDismiss(context, { referencePress: !openOnClick })

  return (
    <Info {...getReferenceProps(rest)} ref={refs.setReference}>
      {children}
      {createPortal(
        <AnimatePresence>
          {infoOpen && (
            <InfoPopup
              ref={refs.setFloating}
              variants={{
                hide: { opacity: 0, scale: 0.7, x: -20, y: -20, pointerEvents: 'none' },
                open: { opacity: 1, scale: 1.0, x: 0, y: 0, pointerEvents: 'all' }
              }}
              initial="hide"
              animate="open"
              exit="hide"
              key="info-popup"
              maxWidth={maxWidth}
              zIndex={zIndex}
              style={{
                top: y || 0,
                left: x || 0,
                position: strategy
              }}
            >
              {renderTooltip ? renderTooltip(infoOpen) : tooltip}
            </InfoPopup>
          )}
        </AnimatePresence>,
        rootRef.current
      )}
    </Info>
  )
}

const Info = styled.span`
  display: flex;
  cursor: pointer;
  display: inline-flex;
  width: fit-content;
  align-items: center;
`

const InfoPopup = styled(motion.div).attrs({
  variants: {
    hide: { opacity: 0, scale: 0.7, x: -20, y: -20, pointerEvents: 'none' },
    open: { opacity: 1, scale: 1.0, x: 0, y: 0, pointerEvents: 'all' }
  },
  initial: 'hide',
  animate: 'open',
  exit: 'hide'
})<{ maxWidth: number; zIndex: number }>`
  z-index: ${({ zIndex }) => zIndex};
  position: absolute;
  background: ${({ theme }) => theme.colors.menuBg};
  color: white;
  box-shadow: 0px 1px 5px 2px rgba(0, 0, 0, 0.14);
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  max-width: ${({ maxWidth }) => maxWidth}px;
  width: fit-content;
  max-height: var(--tooltip-max-height, 400px);
  overflow-y: auto;
  overflow-x: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.2rem;
  font-weight: 500;
`
